import { useState } from "react";

/**
 * FAIL-LOUD banner (rule observability-not-optional). Renders when the
 * pair-watch snapshot is older than STALE_THRESHOLD_MINUTES, or absent.
 * Ported verbatim from
 * products/x2mail-admin/src/components/StaleDataBanner.tsx.
 */
const STALE_THRESHOLD_MINUTES = 90;

export interface StaleDataBannerProps {
  /** ISO-8601 UTC timestamp of the last job tick. Null = no data. */
  updatedAt: string | null;
}

export function StaleDataBanner({ updatedAt }: StaleDataBannerProps) {
  // Capture "now" once on mount (not during render) to satisfy react-hooks/purity.
  // Stale data is a one-way latch — we don't need to re-evaluate on every render.
  const [nowMs] = useState(() => Date.now());

  if (!updatedAt) {
    return (
      <div
        role="alert"
        className="mb-4 flex items-center gap-3 rounded-lg border border-red-700 bg-red-950 px-4 py-3"
      >
        <span className="text-lg">⚠️</span>
        <div>
          <p className="font-semibold text-red-400">METRYKI NIE SPŁYWAJĄ</p>
          <p className="text-sm text-red-300">
            Brak danych ze snapshotu — raportowanie może być zepsute.
          </p>
        </div>
      </div>
    );
  }

  const ageMinutes = (nowMs - new Date(updatedAt).getTime()) / 60_000;
  if (ageMinutes < STALE_THRESHOLD_MINUTES) return null;

  const ageDisplay =
    ageMinutes >= 60
      ? `${Math.round(ageMinutes / 60)}h ${Math.round(ageMinutes % 60)}min`
      : `${Math.round(ageMinutes)} min`;

  return (
    <div
      role="alert"
      className="mb-4 flex items-center gap-3 rounded-lg border border-red-700 bg-red-950 px-4 py-3"
    >
      <span className="text-lg">🔴</span>
      <div>
        <p className="font-semibold text-red-400">
          METRYKI NIE SPŁYWAJĄ — raportowanie może być zepsute
        </p>
        <p className="text-sm text-red-300">
          Ostatni snapshot: <strong>{ageDisplay} temu</strong> (próg: {STALE_THRESHOLD_MINUTES}{" "}
          min). Sprawdź job{" "}
          <code className="rounded bg-red-900 px-1 text-xs">metric_pair_watch</code> na charon.
        </p>
      </div>
    </div>
  );
}

/** Exported for tests. */
export { STALE_THRESHOLD_MINUTES };
